/* eslint-disable */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Button, Typography, CircularProgress, Dialog, DialogContent, IconButton } from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import CloseIcon from '@mui/icons-material/Close';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import './App.css'; // Link to the new CSS file
import StartSplash from './StartSplash';
import IdScanPage from './IdScanPage';
import FaceLivenessPage from './FaceLivenessPage';
import DocScanResultPage from './DocScanResultPage';
import IdTypeSelectionPage from './IdTypeSelectionPage';
import LocationCheckPage from './LocationCheckPage';
import LivenessResultPage from './LivenessResultPage';
import { sendDummyIdScan } from './dummyImage';

function uuidv4() {
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
    ((+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & 15)) >> (+c / 4)).toString(16)
  );
}

function getStateName(state) {
  const stateNames = {
    0: 'Unelevated',
    1: 'Location Verified',
    2: 'ID Scanned/Digital ID Done',
    3: 'Liveness Done',
    4: 'Face Matched',
    5: 'Purchase Unlocked'
  };
  return stateNames[state] || 'Unknown';
}

// Function to get deviceId from URL parameter
function getDeviceIdFromUrl() {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  
  if (!token) {
    console.error('No token parameter found in URL');
    window.location.href = 'https://ageverification.at';
    return null;
  }
  
  // Use token as deviceId
  return token;
}

// Digital flow login redirect function with session bridging
async function redirectToLogin() {
  try {
    console.log('Starting OIDC login with session bridging...');
    
    // Get deviceId from URL parameter
    const deviceId = getDeviceIdFromUrl();
    if (!deviceId) {
      throw new Error('No device ID found in URL token parameter.');
    }
    
    // Get verification session data from hVerifySDK
    const sdk = window.hVerifySDKInstance;
    if (!sdk || !sdk.sessionState) {
      throw new Error('No active verification session found. Please complete location check first.');
    }
    
    const originalSessionId = sdk.sessionState.sessionId;
    
    console.log(`Bridging session: DeviceId ${deviceId}, Original SessionId ${originalSessionId}`);
    
    // Ensure session is established by making a request to current domain first
    await fetch(window.location.origin, {
      method: 'HEAD',
      credentials: 'include'
    });
    
    // Bridge the verification session with the current HTTP session
    const bridgeResponse = await fetch('https://api-main.ageverification.at/oidc/bridge-session', {
      method: 'POST',
      credentials: 'include',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        deviceId: deviceId,
        originalSessionId: originalSessionId,
        source: 'mobile-client'
      })
    });
    
    if (!bridgeResponse.ok) {
      const errorData = await bridgeResponse.json().catch(() => ({ message: 'Bridge request failed' }));
      throw new Error(`Session bridge failed: ${errorData.message || bridgeResponse.statusText}`);
    }
    
    const bridgeData = await bridgeResponse.json();
    console.log('Main server session bridge successful:', bridgeData);
    
    // Also bridge session with e-id-app for potential eID verification
    try {
      const eidBridgeResponse = await fetch('https://eid.ageverification.at/api/bridge-session', {
        method: 'POST',
        credentials: 'include',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          deviceId: deviceId,
          originalSessionId: originalSessionId,
          source: 'mobile-client'
        })
      });
      
      if (eidBridgeResponse.ok) {
        const eidBridgeData = await eidBridgeResponse.json();
        console.log('E-ID app session bridge successful:', eidBridgeData);
      } else {
        console.warn('E-ID app session bridge failed, but continuing:', eidBridgeResponse.statusText);
      }
    } catch (eidError) {
      console.warn('E-ID app session bridge error, but continuing:', eidError.message);
    }
    
    // Small delay to ensure sessions are properly synchronized
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Redirect to OIDC login
    console.log('Redirecting to OIDC login...');
    window.location.href = 'https://api-main.ageverification.at/oidc/login';
    
  } catch (error) {
    console.error('OIDC login setup failed:', error);
    
    // Show user-friendly error message
    if (window.setErrorMessage) {
      window.setErrorMessage(`Login setup failed: ${error.message}. Please try again.`);
    } else {
      alert(`Login setup failed: ${error.message}. Please try again.`);
    }
    
    // Don't proceed with redirect if session bridge fails
    throw error;
  }
}

const theme = createTheme({
  typography: {
    fontFamily: 'Helvetica',
  },
});

// Add a new step for document scan success
const steps = {
  START: 'START',
  LOCATION_CHECK: 'LOCATION_CHECK',
  SCAN_ID: 'SCAN_ID',
  LIVENESS_CHECK: 'LIVENESS_CHECK',
  PROCESSING: 'PROCESSING',
  ERROR: 'ERROR',
  DOCUMENT_ERROR: 'DOCUMENT_ERROR',
  DOCUMENT_SUCCESS: 'DOCUMENT_SUCCESS', // <-- add this
  SUCCESS: 'SUCCESS',
};

const App = () => {
  const [step, setStep] = useState(steps.START);
  const setStepRef = useRef(null); // Create a ref to hold setStep function

  useEffect(() => {
    setStepRef.current = setStep; // Assign setStep to the ref
    window.setStep = setStep; // Optionally, expose setStep globally (use cautiously)
  }, [setStep]); // Run whenever setStep changes

  const [countdown, setCountdown] = useState(10);
  const idFront = useRef(null);
  const idBack = useRef(null);
  const [frontStatus, setFrontStatus] = useState("waiting for Scan !");
  const [backStatus, setBackStatus] = useState("waiting for Scan !");
  const [errorMessage, setErrorMessage] = useState(null);
  const setErrorMessageRef = useRef(null); // Create a ref to hold setErrorMessage
  
  // Add session state tracking
  const [sessionState, setSessionState] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [autoRecoveryAttempts, setAutoRecoveryAttempts] = useState(0);
  const verificationCompletedRef = useRef(false);
  const MAX_RECOVERY_ATTEMPTS = 2;

  useEffect(() => {
    setErrorMessageRef.current = setErrorMessage; // Assign setErrorMessage to the ref
    window.setErrorMessage = setErrorMessage; // Optionally, expose setErrorMessage globally (use cautiously)
  }, [setErrorMessage]);

  const [isTermsOpen, setIsTermsOpen] = useState(false); // State for terms of service overlay
  
  // hVerifySDK ref to persist throughout the app
  const sdkRef = useRef(null);

  const resetApp = () => {
    setStep(steps.START);
    setFrontStatus("waiting for Scan !");
    setBackStatus("waiting for Scan !");
    idFront.current = null;
    idBack.current = null;
    setErrorMessage(null);
    setCountdown(10); // Default countdown, will be overridden based on step
    setSessionState(null);
    setSessionId(null);
    setAutoRecoveryAttempts(0);
    verificationCompletedRef.current = false;
  };
  
  const autoRecoverVerification = (errorStep, errorReason) => {
    if (autoRecoveryAttempts < MAX_RECOVERY_ATTEMPTS) {
      console.log(`Auto-recovery attempt ${autoRecoveryAttempts + 1}/${MAX_RECOVERY_ATTEMPTS} for error at step: ${errorStep}`);
      setAutoRecoveryAttempts(prev => prev + 1);
      
      // Wait a moment then restart verification
      setTimeout(() => {
        console.log('Auto-recovering: restarting verification process...');
        resetApp();
        
        // Clear SDK session state
        if (sdkRef.current) {
          sdkRef.current.sessionState = null;
          sdkRef.current.isAuthenticated = false;
          sdkRef.current._skipInitialVerification = false;
          
          // Restart verification
          setTimeout(() => {
            const deviceId = getDeviceIdFromUrl();
            if (deviceId) {
              sdkRef.current.startVerification();
            }
          }, 1000);
        }
      }, 2000); // 2 second delay before auto-recovery
    } else {
      console.log('Max auto-recovery attempts reached. Manual intervention required.');
      setErrorMessage('Verification failed after multiple attempts. Please refresh the page to try again.');
      setStep(steps.ERROR);
    }
  };

  const handleScanResult = (side, result) => {
    if (side === "front") {
      idFront.current = result;
      setFrontStatus("SUCCESS");
    } else if (side === "back") {
      idBack.current = result;
      setBackStatus("SUCCESS");
    }

    // Only proceed if both sides are scanned
    if (idFront.current !== null && idBack.current !== null) {
      // Here, you would check if the scan is valid or not (simulate with a placeholder)
      // For demonstration, let's assume a function isValidScan returns true/false
      // Replace this with your actual scan validation logic
      const scanIsValid = true; // <-- Replace with real validation
      if (scanIsValid) {
        setStep(steps.DOCUMENT_SUCCESS);
      } else {
        setErrorMessage("Your scan was not successful. Please remove ID and scan again!");
        setStep(steps.DOCUMENT_ERROR);
      }
    }
  };

  useEffect(() => {
    window.handleScanResult = handleScanResult;
    
    // Make setErrorMessage globally accessible for redirectToLogin function
    window.setErrorMessage = setErrorMessage;
    
    // Add hVerifySDK callback handler for ID scan results
    window.handleIdScanResult = (data) => {
      console.log('App.js received ID scan result:', data);
      if (data && data.success) {
        // Update session ID from scan result (no longer dependent on scan completion)
        if (data.sessionId) {
          setSessionId(data.sessionId);
          console.log('Received session ID from scan result:', data.sessionId);
        }
        setStep(steps.DOCUMENT_SUCCESS);
      } else {
        const errorMsg = data.message || "ID scan was not successful.";
        setErrorMessage(errorMsg);
        setStep(steps.DOCUMENT_ERROR);
      }
    };

    // Set up SDK callback handlers when SDK is initialized by IdTypeSelectionPage
    const setupSDKCallbacks = () => {
      if (window.hVerifySDKInstance && !sdkRef.current) {
        sdkRef.current = window.hVerifySDKInstance;

        // Set up ALL available callback listeners from hVerifySDK
        sdkRef.current
          // Connection events
          .onConnect((data) => {
            console.log('hVerifySDK connected:', data);
          })
          .onDisconnect((data) => {
            console.log('hVerifySDK disconnected:', data);
          })
          .onReconnect((data) => {
            console.log('hVerifySDK reconnected:', data);
          })
          .onReconnectAttempt((data) => {
            console.log('hVerifySDK reconnect attempt:', data);
          })
          .onError((error) => {
            console.error('hVerifySDK error:', error);
            setErrorMessage('Connection error: ' + error.error);
          })
          
          // Verification events
          .onVerificationResult((data) => {
            console.log('Verification result:', data);
            if (data.success) {
              if (data.session) {
                setSessionState(data.session);
                // Store session ID immediately since all IDs are now unhashed consistently
                if (data.session.sessionId) {
                  setSessionId(data.session.sessionId);
                  console.log('Session ID received:', data.session.sessionId);
                }
                console.log(`Session state: ${getStateName(data.session.state)}`);
              }
            } else {
              if (data.error === 'DEVICE_BUSY') {
                setErrorMessage(`Device is busy. Try again in ${data.countdownUntilUnlock} seconds.`);
                // Start countdown timer with auto-recovery
                let countdown = data.countdownUntilUnlock;
                const countdownInterval = setInterval(() => {
                  countdown--;
                  if (countdown > 0) {
                    setErrorMessage(`Device is busy. Auto-retrying in ${countdown} seconds.`);
                  } else {
                    clearInterval(countdownInterval);
                    console.log('Device available - auto-recovering...');
                    autoRecoverVerification('session_start', 'device_busy');
                  }
                }, 1000);
              } else {
                console.log('Verification failed - attempting auto-recovery');
                autoRecoverVerification('session_start', data.error);
              }
            }
          })
          .onLocationCheckResult((data) => {
            console.log('Location check result:', data);
            // Forward to LocationCheckPage if handler exists
            if (window.handleLocationCheckResult) {
              window.handleLocationCheckResult(data);
            } else if (data.success) {
              setStep(steps.SCAN_ID);
              // Removed sendDummyIdScan from here, now handled by useEffect
            } else {
              console.log('Location check failed - attempting auto-recovery');
              setErrorMessage('Location verification failed. Auto-retrying...');
              autoRecoverVerification('location_check', data.message || 'Unknown error');
            }
          })
          .onIdScanResult((data) => {
            console.log('ID scan result:', data);
            if (data.success) {
              // Update session ID from scan result
              if (data.sessionId) {
                setSessionId(data.sessionId);
                console.log('Session ID received from scan result:', data.sessionId);
              }
            }
            // Forward to global handler function for processing
            if (window.handleIdScanResult) {
              window.handleIdScanResult(data);
            } else {
              // Fallback if handler not available
              if (data.success) {
                setStep(steps.DOCUMENT_SUCCESS);
              } else {
                console.log('ID scan failed - attempting auto-recovery');
                setErrorMessage('ID scan failed. Auto-retrying...');
                autoRecoverVerification('id_scan', data.message || 'Unknown error');
              }
            }
          })
          .onLivenessCheckResult((data) => {
            console.log('Liveness check result:', data);
            
            // Don't process if verification is already completed
            if (verificationCompletedRef.current) {
              console.log('Ignoring late liveness result - verification already completed');
              return;
            }
            
            if (data.success) {
              // Only go to processing if we're not already at success
              setStep(currentStep => {
                if (currentStep !== steps.SUCCESS) {
                  return steps.PROCESSING;
                }
                return currentStep;
              });
            } else {
              console.log('Liveness check failed - attempting auto-recovery');
              setErrorMessage('Liveness check failed. Auto-retrying...');
              autoRecoverVerification('liveness_check', data.message || 'Unknown error');
            }
          })
          .onFaceMatchResult((data) => {
            console.log('Face match result:', data);
            if (data.success) {
              setStep(steps.SUCCESS);
              verificationCompletedRef.current = true;
            } else {
              console.log('Face matching failed - attempting auto-recovery');
              setErrorMessage('Face matching failed. Auto-retrying...');
              autoRecoverVerification('face_matching', data.message || 'Unknown error');
            }
          })
          .onPurchaseUnlock((data) => {
            console.log('Purchase unlock:', data);
            if (data.success) {
              setStep(steps.SUCCESS);
              verificationCompletedRef.current = true;
            } else {
              console.log('Purchase unlock failed - attempting auto-recovery');
              setErrorMessage('Purchase unlock failed. Auto-retrying...');
              autoRecoverVerification('purchase_unlock', data.message || 'Unknown error');
            }
          })
          .onVerificationComplete((data) => {
            console.log('Verification complete:', data);
            setStep(steps.SUCCESS);
            verificationCompletedRef.current = true;
          })
          .onVerificationFailed((data) => {
            console.log('Verification failed:', data);
            console.log(`Verification failed at step: ${data.step}, reason: ${data.reason}`);
            console.log('General verification failed - attempting auto-recovery');
            setErrorMessage('Verification failed. Auto-retrying...');
            autoRecoverVerification(data.step || 'unknown', data.reason || data.message || 'Unknown error');
          })
          
          // Process and session events
          .onProcessStateUpdate((data) => {
            console.log('Process state update:', data);
            // Handle process step updates
            if (data.step === 'faceMatching' && data.success) {
              console.log('Face matching completed successfully');
            } else if (data.step === 'purchaseUnlock' && data.success) {
              setStep(steps.SUCCESS);
              verificationCompletedRef.current = true;
            }
          })
          .onSessionAutoRestore((data) => {
            console.log('Session auto restore:', data);
            if (data.success) {
              setSessionState(data.session);
              console.log(`Welcome back! Session auto-restored: ${getStateName(data.session.state)}`);
              sdkRef.current._skipInitialVerification = true;
              
              // Navigate to appropriate step based on session state
              const state = data.session.state;
              if (state >= 5) setStep(steps.SUCCESS);
              else if (state >= 4) setStep(steps.PROCESSING);
              else if (state >= 3) setStep(steps.PROCESSING);
              else if (state >= 2) setStep(steps.LIVENESS_CHECK);
              else if (state >= 1) setStep(steps.SCAN_ID);
              else setStep(steps.LOCATION_CHECK);
            }
          })
          .onSessionRestoreResult((data) => {
            console.log('Session restore result:', data);
            if (data.success) {
              setSessionState(data.session);
              console.log(`Session restored! Current state: ${getStateName(data.session.state)}`);
            }
          })
          .onSessionTimeout((data) => {
            console.log('Session timeout:', data);
            console.log(`Session timeout reason: ${data.reason}`);
            console.log('Session timed out - attempting auto-recovery');
            setErrorMessage('Session timed out. Auto-retrying...');
            autoRecoverVerification('session_timeout', data.reason);
          })
          .onSessionExpired((data) => {
            console.log('Session expired:', data);
            console.log('Session expired - attempting auto-recovery');
            setErrorMessage('Session expired. Auto-retrying...');
            autoRecoverVerification('session_expired', 'Session expired');
          })

        // SDK is already available globally from IdTypeSelectionPage
        console.log('SDK callbacks set up successfully');

      } else if (!window.hVerifySDKInstance) {
        // SDK not yet initialized, check again later
        setTimeout(setupSDKCallbacks, 100);
      }
    };

    // Check for SDK initialization periodically
    const checkSDKInterval = setInterval(() => {
      if (window.hVerifySDKInstance && !sdkRef.current) {
        setupSDKCallbacks();
        clearInterval(checkSDKInterval);
      }
    }, 100);

    // Cleanup interval after 10 seconds to avoid infinite checking  
    setTimeout(() => clearInterval(checkSDKInterval), 10000);

    // Expose setupSDKCallbacks globally so IdTypeSelectionPage can call it
    window.setupSDKCallbacks = setupSDKCallbacks;

    return () => {
      // Cleanup on unmount
      if (sdkRef.current) {
        sdkRef.current.disconnect();
      }
      // Remove global references
      delete window.setErrorMessage;
      delete window.handleScanResult;
      delete window.handleIdScanResult;
      delete window.setupSDKCallbacks;
    };
  }, []);


  const handleLivenessCheckResult = useCallback((livenessResult) => {
    console.log("! Liveness RESULT = ", livenessResult);
    if (livenessResult.success) {
      setStep(steps.SUCCESS);
    } else {
      setErrorMessage(livenessResult.error);
      setStep(steps.ERROR);
    }
  }, []);  // No dependencies, as this function doesn't rely on changing variables

  const startLivenessCheck = useCallback(() => {
    // Only proceed if we have the session ID
    if (!sessionId) {
      console.log('Waiting for session ID before starting liveness check...');
      return;
    }

    const container = document.querySelector('#liveness-container');
    let faceLivenessElement = document.querySelector('face-liveness');
    
    // If element doesn't exist, create it
    if (!faceLivenessElement) {
      faceLivenessElement = document.createElement('face-liveness');
      container.appendChild(faceLivenessElement);
    }

    faceLivenessElement.addEventListener('face-liveness', (event) => function () {
      console.log("faceLIVENESSELEMENENT", event.detail);
    }); // event listener for face-liveness component
    
    // Use session ID for liveness component
    const livenessTag = sessionId;
    console.log('Using session ID for liveness component:', sessionId);
    
    // Apply settings AFTER the component is in the DOM
    faceLivenessElement.settings = {
      url: 'https://face.ageverification.at',
      tag: livenessTag,
      livenessType: 1,
      startScreen: true,
      finishScreen: true,
      closeDisabled: true,
      changeCamera: true,
      rotationAngle: 0,
      timeoutInterval: 60,
      detectOcclusion: true,
      holdStillDuration: 1,
      locale: 'de',
      copyright: false,
      finishScreen: false,
      customization: {
        fontFamily: 'Suse',
        fontSize: '16px',
        onboardingScreenStartButtonBackground: '#1f58a1', // Main action color
        onboardingScreenStartButtonBackgroundHover: '#2a6bc7', // Lighter for hover
        onboardingScreenStartButtonTitle: '#FFFFFF',
        onboardingScreenStartButtonTitleHover: '#FFFFFF',
        cameraScreenFrontHintLabelBackground: '#E8E8E8',
        //onboardingScreenIllumination: 'https://path-to-image.com',
        //onboardingScreenAccessories: 'data:image/svg+xml;base64,PHN2...',
        //onboardingScreenCameraLevel: importedImage,
        cameraScreenFrontHintLabelText: '#000000',
        cameraScreenSectorActive: '#1f58a1', // Main action color
        cameraScreenSectorTarget: '#4a7bc8', // Lighter variation
        cameraScreenStrokeNormal: '#1f58a1', // Main action color
        processingScreenProgress: '#1f58a1', // Main action color
        retryScreenRetryButtonBackground: '#1f58a1', // Main action color
        retryScreenRetryButtonBackgroundHover: '#2a6bc7', // Lighter for hover
        retryScreenRetryButtonTitle: '#FFFFFF',
        retryScreenRetryButtonTitleHover: '#FFFFFF',
        //retryScreenEnvironmentImage: 'https://path-to-image.com',
        //retryScreenPersonImage: 'data:image/svg+xml;base64,PHN2...',
        //successScreenImage: importedImage,
      },
    };

    // Component is already appended above

    container.addEventListener('face-liveness', (data) => {
      console.log("liveness event:", data);

      if (data.detail.action === 'PROCESS_FINISHED' && data.detail.data.reason !== 'CHANGE_CAMERA') {
        const responseData = data.detail.data.response;
        
        // Check for liveness success: status === 1 AND code === 0
        if (data.detail.data?.status === 1 && responseData?.code === 0) {
          console.log('Liveness check completed successfully!');
          // Face-liveness component handles its own processing
          // hVerifySDK will receive results from server and update UI via callbacks
        } else {
          // Treat any other combination as liveness error
          console.log('Liveness check failed - invalid response:', {
            status: data.detail.data?.status,
            code: responseData?.code,
            reason: data.detail.data?.reason
          });
          
          // Trigger auto-recovery for liveness failure
          autoRecoverVerification('liveness_check', 'Liveness validation failed');
          
          // Close liveness component
          const faceLiveness = document.querySelector('face-liveness');
          if (faceLiveness) {
            faceLiveness.remove();
          }
        }
      }

      if (data.detail.action === 'CLOSE') {
        const faceLiveness = document.querySelector('face-liveness');
        if (faceLiveness) {
          faceLiveness.remove();
            //setErrorMessage("You canceled Face Validation.");
            //setStep(steps.ERROR);
        }
      }
    });
  }, [handleLivenessCheckResult, sessionId]);

 
  

  useEffect(() => {
    if (step === steps.LIVENESS_CHECK) {
      startLivenessCheck();
    }
  }, [step, startLivenessCheck, sessionId]);

  const handleAbort = () => {
    resetApp(); // Reset the app state
  };

  useEffect(() => {
    if (
      step === steps.SUCCESS ||
      step === steps.ERROR ||
      (step === steps.DOCUMENT_ERROR && false) // disables countdown for DocScanResultPage
    ) {
      // Use different countdown times: 30 seconds for success, 10 seconds for error
      const countdownTime = step === steps.SUCCESS ? 30 : 10;
      
      // Initialize countdown if it's not already set to the correct value
      if (countdown !== countdownTime) {
        setCountdown(countdownTime);
        return; // Exit early to let the next effect run with correct countdown
      }
      
      const interval = setInterval(() => {
        setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);

      if (countdown === 0) {
        resetApp(); // Restart to the start screen
        if (step === steps.DOCUMENT_ERROR) {
          setStep(steps.SCAN_ID);
        }
      }

      return () => clearInterval(interval);
    }
  }, [step, countdown]);

  const renderStepCount = () => {
    if (step === steps.LOCATION_CHECK || step === steps.SCAN_ID || step === steps.LIVENESS_CHECK) {
      const stepCount = {
        LOCATION_CHECK: 1,
        SCAN_ID: 2,
        LIVENESS_CHECK: 3,
      };

      return (
        <Typography
          variant="body2"
          color="text.secondary"
          style={{ marginTop: '1em', position: step === steps.LIVENESS_CHECK ? 'absolute' : 'relative', zIndex: 9999 }}
        >
          Step {stepCount[step]} of 3
        </Typography>
      );
    }
    return null;
  };

  useEffect(() => {
    if (step === steps.SCAN_ID) {
      if (window.selectedFlow === 'digital') {
        // Redirect to digital login after short delay
        const timer = setTimeout(() => {
          redirectToLogin();
        }, 2000);
        return () => clearTimeout(timer);
      } else {
        // Physical flow - send dummy scan after delay
        const timer = setTimeout(() => {
          sendDummyIdScan();
        }, 5000);
        return () => clearTimeout(timer);
      }
    }
  }, [step]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="box-container mobile-full-width">
        
        {step === steps.START && (
          <StartSplash
            onContinue={() => {
              idFront.current = null;
              idBack.current = null;
              setStep('ID_TYPE_SELECTION');
            }}
            onOpenTerms={() => setIsTermsOpen(true)}
          />
        )}
        {step === 'ID_TYPE_SELECTION' && (
          <IdTypeSelectionPage onContinue={() => {
            // SDK will handle verification automatically, just proceed to location check
            setStep(steps.LOCATION_CHECK);
          }} />
        )}
        {step === steps.LOCATION_CHECK && (
          <LocationCheckPage onContinue={() => setStep(steps.SCAN_ID)} />
        )}
        {step === steps.SCAN_ID && window.selectedFlow === 'physical' && (
          <IdScanPage onAbort={handleAbort} />
        )}
        {step === steps.SCAN_ID && window.selectedFlow === 'digital' && (
          <Box className="box-processing">
            <Typography variant="h2" color="text.primary">Redirecting to Digital ID login...</Typography>
            <CircularProgress />
          </Box>
        )}
        {/* Insert new DocScanResultPage for document scan result */}
        {(step === steps.DOCUMENT_ERROR || step === steps.DOCUMENT_SUCCESS) && (
          <DocScanResultPage
            isError={step === steps.DOCUMENT_ERROR}
            errorMessage={errorMessage}
            onRedirect={() => step === steps.DOCUMENT_ERROR ? setStep(steps.START) : setStep(steps.LIVENESS_CHECK)}
          />
        )}
        {step === steps.LIVENESS_CHECK && (
          <FaceLivenessPage onAbort={handleAbort}>
            <div id="liveness-container" className="liveness-container">
              {/* face-liveness component will be created dynamically in startLivenessCheck */}
            </div>
          </FaceLivenessPage>
        )}
        {/* LivenessResultPage integration */}
        {(step === steps.SUCCESS || step === steps.ERROR) && (
          <LivenessResultPage
            isError={step === steps.ERROR}
            errorMessage={errorMessage}
            onContinue={() => {
              if (step === steps.SUCCESS) {
                // Call success URL for external notification
                fetch('https://matatov20ed87z4ehu37dtz238e.ngrok.dev/success', {
                  method: 'GET'
                }).catch(error => {
                  console.log('Success notification failed (non-critical):', error);
                });
                
                resetApp();
                setStep(steps.START);
                //window.location.href = "https://ageverification.at";
              } else {
                resetApp();
                setStep(steps.START);
              }
            }}
          />
        )}
  
        {step === steps.PROCESSING && (
          <Box className="box-processing">
            <Typography variant="h2" color="text.primary">Processing... Please wait</Typography>
            <CircularProgress />
          </Box>
        )}
  
        <Dialog fullScreen open={isTermsOpen} onClose={() => setIsTermsOpen(false)}>
          <DialogContent style={{ padding: '10px', position: 'relative', width: '100vw', maxWidth: '100vw', boxSizing: 'border-box' }}>
            <IconButton
              aria-label="close"
              onClick={() => setIsTermsOpen(false)}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: '#fff',
                boxShadow: 1,
                zIndex: 10
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box display="flex" justifyContent="center" alignItems="center" style={{ marginBottom: '20px' }}>
              <QRCodeCanvas value="https://kloss.pk" size={150} />
            </Box>

            <Typography variant="h4" gutterBottom>Data protection & Terms of Services</Typography>
            <Typography variant="body1" paragraph>
              Protecting your personal data is of utmost importance to us. Our age verification system is designed to comply with the highest data protection standards, including the General Data Protection Regulation (GDPR) and the Austrian Data Protection Act (DSG). Below, we provide a detailed explanation of how we process your data exclusively in Vienna, Austria.
            </Typography>

            <Typography variant="h5" gutterBottom>1. Responsible Party</Typography>
            <Typography variant="body1" paragraph>
              The data controller for the age verification system is:<br/>
              Patrick Kloss<br />
              Krottenbachstraße 111<br />
              1190 Wien<br />
              Österreich<br />
            </Typography>
            <Typography variant="body1" paragraph>
              For any questions or concerns about data protection, please contact: patrick@kloss.pk
            </Typography>

            <Typography variant="h5" gutterBottom>2. Purpose of Data Processing</Typography>
            <Typography variant="body1" paragraph>
              The age verification system is used solely to verify the minimum age of users. The process is secure, automated, and designed to prevent misuse while protecting your privacy.
            </Typography>

            <Typography variant="h5" gutterBottom>3. Data Processed</Typography>
            <Typography variant="body1" paragraph>
              For verification, the following data is temporarily processed:
            </Typography>
            <Typography variant="body1" paragraph>
              <ul>
                <li><strong>Document Data:</strong> Name, date of birth, expiration date, and document number (e.g., from ID card, passport, driver’s license, residence permit, or eCard). A digital portrait from the document is also extracted.</li>
                <li><strong>Facial Data (Face Verification):</strong> A live portrait captured via a camera (e.g., smartphone) to verify that the person is real (anti-spoofing protection).</li>
                <li><strong>Face Matching:</strong> The live portrait is compared with the portrait from the provided document to confirm identity.</li>
              </ul>
              No additional personal data is processed, and the data is used solely for verification purposes.
            </Typography>

            <Typography variant="h5" gutterBottom>4. Storage and Deletion Policy</Typography>
            <Typography variant="body1" paragraph>
              We only process temporarily and do not store your data permanently. All processed data is automatically deleted immediately after verification.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Processing Duration:</strong> Data processing is completed in real time, typically within 15-60 seconds. All data is deleted immediately after the verification process is completed and can not be recovered or seen by other users in the user interface.
            </Typography>

            <Typography variant="h5" gutterBottom>5. Legal Basis for Processing</Typography>
            <Typography variant="body1" paragraph>
              The legal basis for processing your data is Article 6(1)(b) GDPR (performance of a contract) and Article 6(1)(f) GDPR (legitimate interests). Our legitimate interest is to provide a secure and reliable age verification system while preventing misuse.
            </Typography>

            <Typography variant="h5" gutterBottom>6. Location of Data Processing</Typography>
            <Typography variant="body1" paragraph>
              All data processing occurs exclusively in Austria, specifically in secure data centers located in Vienna. No data is transferred or processed outside of Austria.
            </Typography>

            <Typography variant="h5" gutterBottom>7. Security Measures</Typography>
            <Typography variant="body1" paragraph>
              We implement comprehensive technical and organizational measures to protect your data:
            </Typography>
            <Typography variant="body1" paragraph>
              <ul>
                <li><strong>Encryption:</strong> All data transmissions are encrypted (TLS/SSL).</li>
                <li><strong>Access Controls:</strong> Not even our technicians can access the temporarily processed data.</li>
                <li><strong>Data Security:</strong> Biometric and other personal data are processed in isolated and highly secure environments.</li>
              </ul>
            </Typography>

            <Typography variant="h5" gutterBottom>8. Rights of the Data Subject</Typography>
            <Typography variant="body1" paragraph>
              Under the GDPR, you have the following rights:
            </Typography>
            <Typography variant="body1" paragraph>
              <ul>
                <li><strong>Right to Access:</strong> You can request a copy of your data during processing.</li> (This will be empty as we do not save any of your data!)
                <li><strong>Right to Deletion:</strong> As data is automatically deleted, no further deletion requests are necessary.</li>
                <li><strong>Right to Object:</strong> You can object to the processing, though verification will not be possible.</li>
                <li><strong>Right to Data Portability:</strong> Not applicable due to the lack of permanent data storage.</li>
              </ul>
              To exercise your rights, please contact us at patrick@kloss.pk
            </Typography>

            <Typography variant="h5" gutterBottom>9. Automated Decision-Making</Typography>
            <Typography variant="body1" paragraph>
              The age verification system operates entirely automatically. Your data is used solely for age verification and identity confirmation. No manual review of your data occurs.
            </Typography>

            <Typography variant="h5" gutterBottom>10. Data Sharing</Typography>
            <Typography variant="body1" paragraph>
              Your data is not shared with third parties. All processing is conducted exclusively by us using highly secure systems.
            </Typography>

            <Typography variant="h5" gutterBottom>11. Updates to the Data Protection Policy</Typography>
            <Typography variant="body1" paragraph>
              We may update this policy to reflect changes in legal or technical requirements.
            </Typography>

            <Typography variant="body1" paragraph>
              <strong>Effective Date:</strong> 01.12.2024 (dd.mm.yyyy)
            </Typography>
            <Typography variant="body1" paragraph>
              For further information, please contact us. Your trust is important to us, and we are committed to protecting your data.
            </Typography>

          </DialogContent>
        </Dialog>
        
      </Box>
    </ThemeProvider>
  );
  
};

export default App;
